import { useContext, useEffect, useMemo, useRef } from 'react';
import { FiltersContext } from '@Sage/ssm-web5';
import { isEqual } from 'utils/objects';

/**
 * Custom hook to manage filter state synchronization with cache and provide
 * memoized access to filter-related state properties.
 *
 * This hook handles the synchronization between cached filter data and the
 * FiltersContext state, ensuring that changes in cache are reflected in the
 * component state while avoiding unnecessary re-renders through memoization.
 *
 * @param {Object} cache - The main cache object containing filter data
 * @param {Array} cache.quickFilters - Array of quick filter configurations
 * @param {Object} cache.openFilters - Object tracking which filters are open
 * @param {Object} cache.openQuickFilters - Object tracking which quick filters are open
 * @param {Object} customCache - Custom cache object used when avoidStorage is true
 * @param {Array} customCache.visibleFilters - Custom visible filters array
 * @param {Object} customCache.openQuickFilters - Custom open quick filters object
 * @param {Array} customCache.quickFilters - Custom quick filters array
 * @param {Object} customCache.openFilters - Custom open filters object
 * @param {boolean} avoidStorage - Flag to determine whether to use customCache instead of state
 * @param {boolean} useQuickFilters - Flag to determine whether to sync quick filter data
 *
 * @returns {Object} An object containing:
 * @returns {Object} returns.state - The current FiltersContext state
 * @returns {Function} returns.dispatch - The FiltersContext dispatch function
 * @returns {Array} returns.visibleFilters - Memoized visible filters array
 * @returns {Object} returns.openQuickFilters - Memoized open quick filters object
 * @returns {Array} returns.quickFilters - Memoized quick filters array
 * @returns {Object} returns.openFilters - Memoized open filters object
 */
const useFilterState = (cache, customCache, avoidStorage, useQuickFilters) => {
    // Get state and dispatch from FiltersContext
    const { state, dispatch } = useContext(FiltersContext);

    // Refs to track previous cache values for comparison
    const quickFiltersRef = useRef(cache?.quickFilters || []);
    const openFiltersRef = useRef(cache?.openFiltersRef || {});
    const openQuickFiltersRef = useRef(cache?.openQuickFilters || {});

    // Sync local state with cached state when cache values change
    useEffect(() => {
        if (useQuickFilters) {
            // Sync quick filters if they've changed
            if (!isEqual(cache?.quickFilters, quickFiltersRef.current)) {
                dispatch({ type: 'quickFilters', payload: cache?.quickFilters || [] });
            }
            // Sync open quick filters if they've changed
            if (!isEqual(cache?.openQuickFilters, openQuickFiltersRef.current)) {
                dispatch({ type: 'openQuickFilters', payload: cache?.openQuickFilters || {} });
            }
            openQuickFiltersRef.current = cache?.openQuickFilters;
        } else {
            // Sync open filters if they've changed
            if (!isEqual(cache?.openFilters, openFiltersRef.current)) {
                dispatch({ type: 'openFilters', payload: cache?.openFilters || {} });
            }
            openFiltersRef.current = cache?.openFilters;
        }
        // Always update quick filters ref regardless of useQuickFilters flag
        quickFiltersRef.current = cache?.quickFilters;
    }, [
        cache?.openFilters,
        cache?.openQuickFilters,
        cache?.quickFilters,
        dispatch,
        useQuickFilters,
    ]);

    // Memoized state getters - return customCache values when avoidStorage is true

    /** @type {Array} Visible filters array */
    const visibleFilters = useMemo(() => {
        if (avoidStorage && customCache?.visibleFilters) {
            return customCache?.visibleFilters || [];
        }
        return state.visibleFilters;
    }, [state?.visibleFilters, customCache?.visibleFilters, avoidStorage]);

    /** @type {Object} Open quick filters object */
    const openQuickFilters = useMemo(() => {
        if (avoidStorage && customCache?.openQuickFilters) {
            return customCache?.openQuickFilters || {};
        }
        return state.openQuickFilters;
    }, [state?.openQuickFilters, customCache?.openQuickFilters, avoidStorage]);

    /** @type {Array} Quick filters array */
    const quickFilters = useMemo(() => {
        if (avoidStorage && customCache?.quickFilters) {
            return customCache?.quickFilters || [];
        }
        return state.quickFilters;
    }, [state?.quickFilters, customCache?.quickFilters, avoidStorage]);

    /** @type {Object} Open filters object */
    const openFilters = useMemo(() => {
        if (avoidStorage && customCache?.openFilters) {
            return customCache?.openFilters || {};
        }
        return state.openFilters;
    }, [state?.openFilters, customCache?.openFilters, avoidStorage]);

    return {
        state,
        dispatch,
        visibleFilters,
        openQuickFilters,
        quickFilters,
        openFilters,
    };
};

export default useFilterState;
